import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRepo } from '../app/services';
import { useClubInstallers, useClubMutation } from '../data/hooks';
import type { ClubInstallerRow } from '../data/repository';
import { endOfDayIso } from '../lib/expiry';
import { errorText } from '../lib/errors';
import { fmtDate, toDateInput } from '../lib/format';
import { Badge, Button, Dialog, Field, Panel, useToast } from './ui';

/**
 * Clubinstallateur (migratie 0020, persona's 03-11-2026): de clubbeheerder wijst zelf iemand aan die sloten installeert
 * en passen inleert, bijvoorbeeld een technische vrijwilliger of een extern bedrijf. Altijd met een einddatum (max. 1 jaar).
 */

const KIND_LABEL: Record<ClubInstallerRow['kind'], string> = { antreja: 'Antreja', club: 'Door de club aangewezen' };

/** Op de persoonspagina: aanwijzen, verlengen of beëindigen. */
export function PersonInstallerPanel({
  orgId,
  personId,
  personName,
  hasApp,
  editable,
}: {
  orgId: string;
  personId: string;
  personName: string;
  hasApp: boolean;
  editable: boolean;
}) {
  const repo = useRepo();
  const toast = useToast();
  const installers = useClubInstallers(orgId, editable);
  const [dialog, setDialog] = useState<'assign' | 'end' | null>(null);
  const end = useClubMutation(orgId, () => repo.endClubInstaller(personId));
  if (!editable) return null;
  const row = installers.data?.find((r) => r.person_id === personId);

  return (
    <Panel
      title="Installateur"
      description="Een installateur van de club mag met de Antreja-app sloten installeren, resetten en passen of vingerafdrukken inleren bij jullie deuren."
      actions={
        <span className="row">
          {row?.kind !== 'antreja' && hasApp && (
            <Button size="sm" variant={row ? 'secondary' : 'primary'} onClick={() => setDialog('assign')}>
              {row ? 'Einddatum wijzigen' : 'Aanwijzen als installateur'}
            </Button>
          )}
          {row?.kind === 'club' && (
            <Button size="sm" variant="danger" onClick={() => setDialog('end')}>
              Beëindigen
            </Button>
          )}
        </span>
      }
    >
      <div className="panel-body row" style={{ gap: 8 }}>
        {row ? (
          <>
            <Badge tone={row.active ? 'green' : 'neutral'}>{row.active ? 'Installateur' : 'Verlopen'}</Badge>
            <span className="muted small">
              {KIND_LABEL[row.kind]}, tot en met {fmtDate(row.valid_until)}
            </span>
          </>
        ) : hasApp ? (
          <span className="muted small">Geen installateur.</span>
        ) : (
          <span className="muted small">Nodig {personName} eerst uit voor de app; installeren gaat met de app.</span>
        )}
      </div>

      {dialog === 'assign' && (
        <AssignDialog orgId={orgId} personId={personId} personName={personName} current={row?.valid_until ?? null} onClose={() => setDialog(null)} />
      )}
      {dialog === 'end' && (
        <Dialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          title="Installateur beëindigen"
          description={`${personName} kan daarna geen sloten meer installeren of resetten en geen passen meer inleren. Ingeleerde passen blijven werken.`}
        >
          {end.error && (
            <div className="alert alert-danger" role="alert">
              {errorText(end.error, 'Niet gelukt.')}
            </div>
          )}
          <div className="dialog-actions">
            <Button onClick={() => setDialog(null)}>Annuleren</Button>
            <Button
              variant="danger"
              disabled={end.isPending}
              onClick={async () => {
                try {
                  await end.mutateAsync(undefined);
                  toast(`${personName} is geen installateur meer.`);
                  setDialog(null);
                } catch {
                  /* melding staat in de dialoog */
                }
              }}
            >
              Beëindigen
            </Button>
          </div>
        </Dialog>
      )}
    </Panel>
  );
}

function AssignDialog({
  orgId,
  personId,
  personName,
  current,
  onClose,
}: {
  orgId: string;
  personId: string;
  personName: string;
  current: string | null;
  onClose: () => void;
}) {
  const repo = useRepo();
  const toast = useToast();
  const today = new Date();
  const max = new Date(today.getTime() + 365 * 86400_000);
  const [date, setDate] = useState(() => toDateInput(current ? new Date(current) : new Date(today.getTime() + 90 * 86400_000)));
  const [error, setError] = useState<string | null>(null);
  const save = useClubMutation(orgId, (until: string) => repo.assignClubInstaller(personId, until));

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={current ? 'Einddatum wijzigen' : `${personName} aanwijzen als installateur`}
      description="Kies tot wanneer. Na die datum vervalt het vanzelf; je kunt altijd verlengen. Installeren vraagt in de app om tweestapsverificatie."
    >
      <form
        className="stack"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (!date || date < toDateInput(today) || date > toDateInput(max)) {
            setError('Kies een datum vanaf vandaag en binnen een jaar.');
            return;
          }
          setError(null);
          try {
            await save.mutateAsync(endOfDayIso(date));
            toast(current ? 'Einddatum gewijzigd.' : `${personName} is installateur tot en met ${fmtDate(endOfDayIso(date))}.`);
            onClose();
          } catch (err) {
            setError(errorText(err, 'Niet gelukt.'));
          }
        }}
      >
        <Field label="Installateur tot en met" error={error}>
          {(p) => (
            <input
              {...p}
              className="input"
              type="date"
              min={toDateInput(today)}
              max={toDateInput(max)}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ maxWidth: 220 }}
            />
          )}
        </Field>
        <div className="dialog-actions">
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>
            {current ? 'Opslaan' : 'Aanwijzen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Instellingen: wie mag bij deze club installeren. */
export function ClubInstallersPanel({ orgId, visible }: { orgId: string; visible: boolean }) {
  const installers = useClubInstallers(orgId, visible);
  if (!visible) return null;
  const rows = installers.data ?? [];
  return (
    <Panel
      title="Installateurs"
      description={
        <>
          Wie met de app sloten mag installeren, resetten en passen mag inleren. Een installateur van de club wijs je aan op de pagina
          van die persoon (bij <strong>Personen</strong>).
        </>
      }
    >
      {installers.isLoading ? (
        <p className="tile-empty">Laden…</p>
      ) : installers.error ? (
        <p className="tile-empty">{errorText(installers.error, 'Kon de installateurs niet laden.')}</p>
      ) : rows.length === 0 ? (
        <p className="tile-empty">Nog geen installateurs.</p>
      ) : (
        <ul className="tile-list">
          {rows.map((r) => (
            <li key={r.profile_id}>
              <span>
                {r.person_id ? (
                  <Link className="primary" to={`/c/${orgId}/personen/${r.person_id}`}>
                    {r.full_name}
                  </Link>
                ) : (
                  <span className="primary">{r.full_name}</span>
                )}
                <span className="cell-sub">
                  {KIND_LABEL[r.kind]}, tot en met {fmtDate(r.valid_until)}
                </span>
              </span>
              <Badge tone={r.active ? 'green' : 'neutral'}>{r.active ? 'Actief' : 'Verlopen'}</Badge>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
